// CandleBuilder — Aggregates WebSocket ticks into candles, merges with REST historical data
// Pure JS class — no React dependencies

import { TIMEFRAME_SECONDS } from '../../../config/tradingConstants';

// IST offset in seconds (UTC+5:30)
const IST_OFFSET = 19800;

export function parseTimeToUTC(timeStr) {
  // "2026-02-28 10:15" → UTC timestamp (seconds). Treat as IST (UTC+5:30).
  const [datePart, timePart] = timeStr.split(' ');
  if (!datePart) return 0;
  const [y, m, d] = datePart.split('-').map(Number);
  if (timePart) {
    const [h, min] = timePart.split(':').map(Number);
    return Date.UTC(y, m - 1, d, h, min, 0) / 1000 - IST_OFFSET;
  }
  return Date.UTC(y, m - 1, d) / 1000;
}

export default class CandleBuilder {
  constructor(timeframe = 'FIFTEEN_MINUTE') {
    this.candles = new Map(); // utcTimestamp → candle
    this.sortedKeys = [];
    this.intervalSec = TIMEFRAME_SECONDS[timeframe] || 900;
  }

  setTimeframe(timeframe) {
    this.intervalSec = TIMEFRAME_SECONDS[timeframe] || 900;
    this.reset();
  }

  reset() {
    this.candles.clear();
    this.sortedKeys = [];
  }

  // Ingest REST historical data — sorts, deduplicates, converts IST→UTC
  setHistoricalCandles(rawCandles) {
    this.candles.clear();
    if (!rawCandles || rawCandles.length === 0) {
      this.sortedKeys = [];
      return;
    }

    const sorted = [...rawCandles].sort((a, b) => (a.time < b.time ? -1 : a.time > b.time ? 1 : 0));

    sorted.forEach(c => {
      const time = parseTimeToUTC(c.time);
      if (this.candles.has(time)) return; // deduplicate
      this.candles.set(time, {
        time,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
        volume: c.volume || 0,
      });
    });

    this._rebuildKeys();
  }

  // Process a live WebSocket tick → returns { updated, candle, isNewBar }
  processTick(tick) {
    if (!tick || !tick.ltp) return { updated: false, candle: null, isNewBar: false };

    const nowUTC = Math.floor(Date.now() / 1000);
    const barTime = this._alignToInterval(nowUTC);
    const existing = this.candles.get(barTime);

    if (existing) {
      // Update existing bar
      existing.high = Math.max(existing.high, tick.ltp);
      existing.low = Math.min(existing.low, tick.ltp);
      existing.close = tick.ltp;
      if (tick.volume !== undefined) existing.volume = tick.volume;
      return { updated: true, candle: { ...existing }, isNewBar: false };
    }

    // New bar
    const newCandle = {
      time: barTime,
      open: tick.ltp,
      high: tick.ltp,
      low: tick.ltp,
      close: tick.ltp,
      volume: tick.volume || 0,
    };
    this.candles.set(barTime, newCandle);
    this._rebuildKeys();
    return { updated: true, candle: { ...newCandle }, isNewBar: true };
  }

  // Returns sorted OHLCV array for series.setData()
  getCandles() {
    return this.sortedKeys.map(k => this.candles.get(k));
  }

  // Returns last bar for series.update()
  getLatestCandle() {
    if (this.sortedKeys.length === 0) return null;
    const last = this.candles.get(this.sortedKeys[this.sortedKeys.length - 1]);
    return last ? { ...last } : null;
  }

  // Returns colored volume array for HistogramSeries
  getVolumeData() {
    return this.sortedKeys.map(k => {
      const c = this.candles.get(k);
      return {
        time: c.time,
        value: c.volume,
        color: c.close >= c.open ? 'rgba(22, 163, 74, 0.3)' : 'rgba(239, 68, 68, 0.3)',
      };
    });
  }

  // Align a UTC seconds timestamp to IST market interval boundaries
  _alignToInterval(utcSeconds) {
    if (this.intervalSec >= 86400) {
      // Daily: align to start of IST day
      const istSeconds = utcSeconds + IST_OFFSET;
      const dayStart = Math.floor(istSeconds / 86400) * 86400;
      return dayStart - IST_OFFSET;
    }
    // Intraday: align to IST market open (09:15 = 33300 sec from IST midnight)
    const istSeconds = utcSeconds + IST_OFFSET;
    const istDayStart = Math.floor(istSeconds / 86400) * 86400;
    const marketOpenIST = istDayStart + 33300; // 9:15 AM IST
    const secSinceOpen = istSeconds - marketOpenIST;
    if (secSinceOpen < 0) return (istDayStart - IST_OFFSET); // pre-market
    const aligned = marketOpenIST + Math.floor(secSinceOpen / this.intervalSec) * this.intervalSec;
    return aligned - IST_OFFSET;
  }

  _rebuildKeys() {
    this.sortedKeys = [...this.candles.keys()].sort((a, b) => a - b);
  }
}
